import { AiMark, Button, Card, CardBody, CardHeader, InlineMessage, Spinner, Stack, Text, TextLink } from '@olytic/foundation';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { fetchStrategy, processStrategy, runStrategy } from './strategyApi';
import {
  citationHref, type Citation, isPending, type Point, pointsOf, runCaption, STRATEGY_SECTIONS, type StrategyRun, type StrategyState,
} from './strategyRuns';

const KIND_LABEL: Record<Citation['kind'], string> = {
  guideline: 'Playbook',
  opportunity: 'Opportunity',
  quote: 'Quote',
  contact: 'Contact',
  meeting: 'Meeting',
  activity: 'Activity',
  history: 'Field History',
};

const messageOf = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

/**
 * An opportunity's strategy and pre-mortem, shared by the header's Strategy
 * action and the Strategy tab. `opportunityId` null (another object) does
 * nothing. Queued runs (the pre-mortem written at Propose) run on load.
 */
export function useOpportunityStrategy(opportunityId: string | null) {
  const [state, setState] = useState<StrategyState | null>(null);
  const [busy, setBusy] = useState<'strategy' | 'pre_mortem' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const processing = useRef(false);

  const process = useCallback(async () => {
    if (!opportunityId || processing.current) return;
    processing.current = true;
    try {
      setState(await processStrategy(opportunityId));
    } catch (e) {
      setError(messageOf(e, 'The pre-mortem could not run.'));
    } finally {
      processing.current = false;
    }
  }, [opportunityId]);

  useEffect(() => {
    if (!opportunityId) return;
    const controller = new AbortController();
    fetchStrategy(opportunityId, { signal: controller.signal })
      .then((loaded) => {
        setState(loaded);
        const queued = [loaded.strategy, loaded.preMortem, loaded.preMortemAtPropose].some((run) => run?.state === 'queued');
        if (queued && loaded.configured) void process();
      })
      .catch((e: unknown) => { if (!controller.signal.aborted) setError(messageOf(e, 'The strategy could not load.')); });
    return () => controller.abort();
  }, [opportunityId, process]);

  const run = useCallback(async (kind: 'strategy' | 'pre_mortem'): Promise<StrategyRun | null> => {
    if (!opportunityId) return null;
    setBusy(kind);
    setError(null);
    try {
      const { run: done, configured } = await runStrategy(opportunityId, kind);
      setState((current) => ({
        configured,
        model: current?.model ?? null,
        strategy: kind === 'strategy' ? done : current?.strategy ?? null,
        preMortem: kind === 'pre_mortem' ? done : current?.preMortem ?? null,
        preMortemAtPropose: current?.preMortemAtPropose ?? null,
      }));
      return done;
    } catch (e) {
      setError(messageOf(e, 'It could not run.'));
      return null;
    } finally {
      setBusy(null);
    }
  }, [opportunityId]);

  /** After the stage changes: reaching Propose queues the pre-mortem, so run it. */
  const afterStageChange = useCallback((stage: string) => {
    if (stage === 'propose') void process();
  }, [process]);

  return { state, busy, error, run, afterStageChange };
}

export type OpportunityStrategy = ReturnType<typeof useOpportunityStrategy>;

function Citations({ citations }: { citations: Citation[] }) {
  return (
    <Text as="span" size="sm" tone="muted">
      {citations.map((citation, i) => {
        const href = citationHref(citation);
        const label = `${KIND_LABEL[citation.kind] ?? citation.kind} · ${citation.label}`;
        return (
          <Fragment key={`${citation.ref}:${i}`}>
            {i > 0 ? ', ' : null}
            {href ? (
              <TextLink asChild>
                <Link to={href} {...(citation.quote ? { title: citation.quote } : {})}>{label}</Link>
              </TextLink>
            ) : (
              <span {...(citation.quote ? { title: citation.quote } : {})}>{label}</span>
            )}
          </Fragment>
        );
      })}
    </Text>
  );
}

function Points({ points, empty }: { points: Point[]; empty: string }) {
  if (points.length === 0) return <Text size="sm" tone="muted">{empty}</Text>;
  return (
    <Stack gap="sm">
      {points.map((point, i) => (
        <Stack key={i} gap="xs">
          <Text size="sm">{point.text}</Text>
          <Citations citations={point.citations} />
        </Stack>
      ))}
    </Stack>
  );
}

function RunStatus({ run, configured, busy }: { run: StrategyRun | null; configured: boolean; busy: boolean }) {
  if (busy) return <Spinner label="Reading the deal and the playbook. This takes a minute." />;
  if (run?.state === 'error') return <InlineMessage tone="error" role="alert">{run.error ?? 'The run failed.'} Run it again to retry.</InlineMessage>;
  if (isPending(run) && !configured) {
    return <InlineMessage tone="info" role="status">The AI service is not set up here yet, so this run is waiting in the queue.</InlineMessage>;
  }
  if (isPending(run)) return <Spinner label="Running." />;
  return null;
}

/** The Strategy tab: the latest strategy, and the pre-mortem. */
export function StrategyTab({ strategy, closed }: { strategy: OpportunityStrategy; closed: boolean }) {
  const { state, busy, error, run } = strategy;
  if (!state) {
    return error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : <Spinner label="Loading the strategy…" />;
  }
  const latest = state.strategy;
  const preMortem = state.preMortem;
  const done = latest?.state === 'done' ? latest : null;
  const preMortemDone = preMortem?.state === 'done' ? preMortem : null;
  return (
    <Stack gap="md">
      {error ? <InlineMessage tone="error" role="alert">{error}</InlineMessage> : null}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)] items-start">
        <Card aria-label="Opportunity Strategy">
          <CardHeader
            icon={<AiMark />}
            title="Opportunity Strategy"
            headingLevel={3}
            actions={
              <Button variant="neutral" size="sm" loading={busy === 'strategy'} disabled={busy !== null} onClick={() => void run('strategy')}>
                {latest ? 'Run Again' : 'Run Strategy'}
              </Button>
            }
          >
            <Text size="sm" tone="muted">{runCaption(latest, state.configured)}</Text>
          </CardHeader>
          <CardBody>
            <Stack gap="md">
              <RunStatus run={latest} configured={state.configured} busy={busy === 'strategy'} />
              {done?.output?.summary ? <Text size="sm">{done.output.summary}</Text> : null}
              {done ? (
                STRATEGY_SECTIONS.map((section) => (
                  <Stack key={section.key} gap="xs">
                    <Text as="h3" size="sm" tone="muted">{section.label}</Text>
                    <Points points={pointsOf(done, section.key)} empty="Nothing here." />
                  </Stack>
                ))
              ) : !latest ? (
                <Text size="sm" tone="muted">
                  Reads this deal, its primary quote, meetings, activity, contacts and history against the playbook, and says how to win it.
                </Text>
              ) : null}
            </Stack>
          </CardBody>
        </Card>
        <Card aria-label="Pre-mortem">
          <CardHeader
            title="Pre-mortem"
            headingLevel={3}
            actions={
              <Button variant="neutral" size="sm" loading={busy === 'pre_mortem'} disabled={busy !== null} onClick={() => void run('pre_mortem')}>
                {preMortem ? 'Run Again' : 'Write Pre-mortem'}
              </Button>
            }
          >
            <Text size="sm" tone="muted">{preMortem ? runCaption(preMortem, state.configured) : 'Written when the deal reaches Propose'}</Text>
          </CardHeader>
          <CardBody>
            <Stack gap="md">
              <RunStatus run={preMortem} configured={state.configured} busy={busy === 'pre_mortem'} />
              {preMortemDone ? (
                <>
                  <Text size="sm" tone="muted">How we would lose this deal</Text>
                  {preMortemDone.output?.summary ? <Text size="sm">{preMortemDone.output.summary}</Text> : null}
                  <Points points={pointsOf(preMortemDone, 'risks')} empty="No risks written." />
                </>
              ) : null}
              {!preMortem ? (
                <Text size="sm" tone="muted">
                  How we would lose this deal, written when it first reaches Propose and set against the Deal Retro at close.
                </Text>
              ) : null}
              {state.preMortemAtPropose && preMortem && state.preMortemAtPropose.id !== preMortem.id ? (
                <Text size="sm" tone="muted">The pre-mortem written at Propose is kept for the Deal Retro.</Text>
              ) : null}
              {closed && preMortemDone ? <Text size="sm" tone="muted">At close, the playbook review sets this against the Deal Retro.</Text> : null}
            </Stack>
          </CardBody>
        </Card>
      </div>
    </Stack>
  );
}
